// Shared event-table chrome — aligned with `_new/detection/WAFEvent.js`.
import React from "react";
import {
  Box, Stack, Typography, Table, TableCell, TableRow, TableBody, Pagination, Tooltip, IconButton,
} from "@mui/material";

import colors from "../../../../../../utils/colors";
import LayerdIcon from "../../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../../icons/detectionLucideIcons";
import { tableHeadSx, tableBodySx } from "../../../detection/WAFEvent";
import { asecFindingsTableSx, asecEventRowSx, asecMonoBlueSx, asecIconButtonSx } from "../asecUi";

const INDIGO = colors.primary[300];
const INDIGO_INK = colors.primary[500];
const INDIGO_SOFT = "rgba(99,113,247,0.10)";

export const ASEC_EVENTS_TABLE_CONTAINER_SX = {
  border: `solid 1px ${colors.neutral[200]}`,
  borderRadius: "8px",
  p: 2,
  overflow: "hidden",
};

export function AsecEventsTableShell({ children, banner, footer, sx }) {
  return (
    <Box sx={{ ...ASEC_EVENTS_TABLE_CONTAINER_SX, ...sx }}>
      {banner}
      <Table sx={asecFindingsTableSx}>{children}</Table>
      {footer}
    </Box>
  );
}

export function AsecEventsHeadCell({ children, align = "left", width, sx }) {
  return (
    <TableCell align={align} sx={{ width, ...sx }}>
      {children ? <Typography sx={tableHeadSx}>{children}</Typography> : null}
    </TableCell>
  );
}

export function AsecEventsBodyText({ children, sx, component, ...rest }) {
  return (
    <Typography component={component} sx={{ ...tableBodySx, ...sx }} {...rest}>
      {children}
    </Typography>
  );
}

export function AsecEventsMonoLine({ children, title, truncate = 36 }) {
  const text = children || "—";
  const str = String(text);
  const showTip = str.length > truncate;
  const display = showTip ? `${str.slice(0, truncate)}…` : str;
  const line = (
    <Typography variant="textSmall" sx={asecMonoBlueSx}>
      {display}
    </Typography>
  );
  return showTip ? <Tooltip title={str} arrow>{line}</Tooltip> : line;
}

/** Hostname column — fall back to site id when host is absent. */
export function asecHostnameLabel(host, siteId) {
  if (host) return host;
  if (siteId != null && siteId !== "") return String(siteId);
  return "—";
}

export function AsecEventsHostnameCell({ host, siteId, sx }) {
  return (
    <AsecEventsBodyText sx={sx}>{asecHostnameLabel(host, siteId)}</AsecEventsBodyText>
  );
}

/** Method + path stack — matches `_new/detection/WAFEvent.js` Activity column. */
export function AsecEventsActivityCell({ method, path, truncate = 36 }) {
  return (
    <Stack>
      <AsecEventsBodyText>{method || "—"}</AsecEventsBodyText>
      <AsecEventsMonoLine truncate={truncate}>{path || "—"}</AsecEventsMonoLine>
    </Stack>
  );
}

export function AsecEventsViewCell({ onClick, title = "View details" }) {
  const icon = <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />;
  return (
    <TableCell sx={{ width: 48 }} onClick={(e) => e.stopPropagation()}>
      {typeof onClick === "function" ? (
        <Tooltip title={title} arrow>
          <IconButton
            size="small"
            aria-label={title}
            onClick={(e) => {
              e.stopPropagation();
              onClick(e);
            }}
            sx={{ ...asecIconButtonSx, p: 0.5 }}
          >
            {icon}
          </IconButton>
        </Tooltip>
      ) : (
        icon
      )}
    </TableCell>
  );
}

export function AsecEventsEmptyRow({ colSpan, message = "There are no events" }) {
  return (
    <TableBody>
      <TableRow>
        <TableCell colSpan={colSpan}>
          <Typography textAlign="center" sx={tableBodySx}>
            {message}
          </Typography>
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

export function AsecEventsTableFooter({
  from,
  to,
  total,
  label = "events",
  page,
  pageCount,
  onPageChange,
  summary,
}) {
  const rangeText = total > 0
    ? `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`
    : `0 ${label}`;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ pt: 2, mt: 1, borderTop: `1px solid ${colors.neutral[200]}` }}
    >
      <Typography sx={{ fontSize: 12, color: colors.neutral[700], fontVariantNumeric: "tabular-nums" }}>
        {summary || (
          <>
            Showing <strong>{rangeText}</strong> {label}
          </>
        )}
      </Typography>
      {typeof page === "number" && pageCount > 1 && onPageChange ? (
        <Pagination
          count={pageCount}
          page={page + 1}
          onChange={(_e, p) => onPageChange(p - 1)}
          size="small"
          shape="rounded"
          sx={{ "& .Mui-selected": { backgroundColor: `${INDIGO_SOFT} !important`, color: INDIGO_INK } }}
        />
      ) : null}
    </Stack>
  );
}

export { tableHeadSx, tableBodySx, asecEventRowSx, asecFindingsTableSx };
